import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Slot group definitions ───────────────────────────────────────────────────

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
      suggestedFields: [
        'photo_gallery[0].image',
        'photo_gallery[1].image',
        'property_featured_image',
      ],
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Amenities banner',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function AmenitiesHero() {
  const bgImage = useSlotGroup('amenitieshero.bg_image', bgImageDef);

  return (
    <section className="relative w-full h-[65vh] md:h-[70vh] min-h-[420px] max-h-[780px] overflow-hidden">

      {/* Background image */}
      <EditableSlotGroup
        groupPath="amenitieshero.bg_image"
        definition={bgImageDef}
        className="absolute inset-0"
      >
        {bgImage.image ? (
          <VibesitesImage
            url={bgImage.image}
            alt={bgImage.alt || ''}
            priority
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-[var(--vibe-color-text)]" />
        )}
      </EditableSlotGroup>

      {/* Dark gradient overlay — heavier at the bottom for left-aligned text */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

      {/* Content — left-aligned, bottom-anchored */}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-12 md:pb-16">
        <RichTextContent
          path="amenitieshero.title"
          label="Title"
          defaultValue="<p>Amenities</p>"
          as="h1"
          className="font-heading text-4xl sm:text-5xl md:text-6xl font-normal text-white leading-[1.1] mb-4"
        />

        <RichTextContent
          path="amenitieshero.subtitle"
          label="Subtitle"
          defaultField="property_home_amenities_blurb"
          as="p"
          className="font-body text-base md:text-lg text-white/90 max-w-3xl leading-relaxed"
        />
      </div>

      {/* Bottom accent line — secondary brand color */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[var(--vibe-color-secondary)]" />
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesHero',
  displayName: 'Amenities Hero',
  tokens: {
    'amenitieshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Amenities</p>',
    },
    'amenitieshero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultField: 'property_home_amenities_blurb',
      generator: {
        prompt:
          'Write a short teaser of the community amenities visitors will find further down this page.',
        context: 'Subtitle under the Amenities page hero title.',
        length: '2 sentences',
      },
    },
  },
  slotGroups: {
    'amenitieshero.bg_image': bgImageDef,
  },
};

(AmenitiesHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default AmenitiesHero;
